/**
 * ============================================================================
 * GELİŞTİRME SAHTESİ — ÜRETİM KODU DEĞİLDİR.
 * ============================================================================
 * `LocalRepository` sözleşmesinin bellek-içi uygulaması. Amacı tek şey: C
 * şeridi (SQLite + OPFS + Worker) tamamlanmadan arayüzün tüm ekranlarının
 * gerçek asenkron davranışla çalışabilmesi ve test edilebilmesi.
 *
 * C şeridi bittiğinde `src/repo/createRepository.ts` içindeki TEK satır
 * değişir; bu dosyaya arayüzün hiçbir yerinden `import` yoktur (yalnızca o
 * fabrika ve testler kullanır).
 *
 * Kalıcılık yok, göç yok, kilit yok. Sözleşmenin belirsiz bıraktığı yerlerde
 * (örn. `positions()` hangi türleri toplar) burada yapılan yorum bir KARAR
 * değil, geçici bir okumadır — gerçek uygulama C şeridinde.
 */

import type {
  Account,
  IceAktarmaDurumu,
  IceAktarmaSonucu,
  ImportResult,
  LocalRepository,
  ManualPrice,
  ManualValuation,
  NewAccount,
  NewManualValuation,
  NewTransaction,
  RawPosition,
  RepositoryInfo,
  RpcError,
  TransactionFilter,
  TransactionPage,
  HesapHareketSecimi,
} from "@parafon/contracts/local-api";
import type { AssetBinding, IsoDate, Transaction } from "@parafon/contracts";
import { assetKey } from "../lib/format";
import { decimalSum, decimalNegate } from "../lib/decimal";
import { cashSign, isQuantityBearing, quantitySign } from "../lib/txShape";

export interface FakeRepositoryOptions {
  /** Her çağrıya eklenen yapay gecikme (ms). Yükleniyor durumlarını görmek için. */
  readonly latencyMs?: number;
  /** `open()` bu hatayla reddedilir — kilit/OPFS ekranlarını denemek için. */
  readonly failOpenWith?: RpcError["code"];
  readonly seed?: {
    readonly accounts?: readonly NewAccount[];
    readonly transactions?: readonly NewTransaction[];
    readonly manualValuations?: readonly NewManualValuation[];
    readonly manualPrices?: readonly ManualPrice[];
  };
}

const SCHEMA_VERSION = 1;

function rpc(code: RpcError["code"], message: string, detail?: string): Error & { cause: RpcError } {
  const error = new Error(message) as Error & { cause: RpcError };
  error.cause = detail === undefined ? { code, message } : { code, message, detail };
  return error;
}

export function createFakeRepository(options: FakeRepositoryOptions = {}): LocalRepository {
  const latency = options.latencyMs ?? 0;
  const deviceId = "dev-fake-device";

  const accounts = new Map<string, Account>();
  const transactions = new Map<string, Transaction>();

  function hareketleriSilYaDaTasi(kaynak: string, secim: HesapHareketSecimi): number {
    if (secim.tur === "tasi") {
      const h = accounts.get(secim.hedefHesapId);
      if (secim.hedefHesapId === kaynak) throw rpc("VALIDATION", "Hareketler aynı hesaba taşınamaz.");
      if (h === undefined || h.deleted) throw rpc("VALIDATION", "Taşınacak hesap yok ya da silinmiş.");
    }
    let n = 0;
    for (const t of [...transactions.values()]) {
      if (t.accountId !== kaynak || t.deleted) continue;
      const ortak = { lamport: tick(), updatedAt: new Date().toISOString() };
      transactions.set(
        t.id,
        secim.tur === "sil" ? { ...t, ...ortak, deleted: true } : { ...t, ...ortak, accountId: secim.hedefHesapId },
      );
      n += 1;
    }
    return n;
  }
  const valuations = new Map<string, ManualValuation>();
  const prices = new Map<string, ManualPrice>();
  const settings = new Map<string, string>();

  let lamport = 0;
  let opened = false;

  const tick = (): number => {
    lamport += 1;
    return lamport;
  };

  const wait = async (): Promise<void> => {
    if (latency > 0) await new Promise((r) => setTimeout(r, latency));
  };

  const requireOpen = (): void => {
    if (!opened) throw rpc("UNKNOWN", "Depo açılmadan kullanıldı.");
  };

  const stamp = <T extends { lamport: number; deviceId: string; deleted: boolean; updatedAt: string }>(
    base: Omit<T, "lamport" | "deviceId" | "deleted" | "updatedAt">,
    deleted = false,
  ): T =>
    ({
      ...base,
      lamport: tick(),
      deviceId,
      deleted,
      updatedAt: new Date().toISOString(),
    }) as T;

  const priceKey = (instrumentRef: string, date: IsoDate): string => `${instrumentRef}\u0000${date}`;

  function seedNow(): void {
    for (const a of options.seed?.accounts ?? []) accounts.set(a.id, stamp<Account>(a));
    for (const t of options.seed?.transactions ?? []) transactions.set(t.id, stamp<Transaction>(t));
    for (const v of options.seed?.manualValuations ?? []) valuations.set(v.id, stamp<ManualValuation>(v));
    for (const p of options.seed?.manualPrices ?? []) prices.set(priceKey(p.instrumentRef, p.date), p);
  }

  function matches(t: Transaction, f: TransactionFilter): boolean {
    if (!f.includeDeleted && t.deleted) return false;
    if (f.accountIds && f.accountIds.length > 0 && !f.accountIds.includes(t.accountId)) return false;
    if (f.types && f.types.length > 0 && !f.types.includes(t.type)) return false;
    if (f.from !== undefined && t.tradeDate < f.from) return false;
    if (f.to !== undefined && t.tradeDate > f.to) return false;
    if (f.instrumentIds && f.instrumentIds.length > 0) {
      if (t.asset.kind !== "tracked" || !f.instrumentIds.includes(t.asset.instrumentId)) return false;
    }
    if (f.manualLabels && f.manualLabels.length > 0) {
      if (t.asset.kind !== "manual" || !f.manualLabels.includes(t.asset.label)) return false;
    }
    return true;
  }

  const repo: LocalRepository = {
    async open(): Promise<RepositoryInfo> {
      await wait();
      if (options.failOpenWith) {
        const messages: Record<RpcError["code"], string> = {
          LOCKED_BY_OTHER_TAB: "Veritabanı başka bir sekme tarafından kilitli.",
          OPFS_UNAVAILABLE: "OPFS bu ortamda kullanılamıyor.",
          MIGRATION_FAILED: "Şema göçü başarısız.",
          NOT_FOUND: "Bulunamadı.",
          VALIDATION: "Doğrulama hatası.",
          TIMEOUT: "İstek zaman aşımına uğradı.",
          SERVER_UNAVAILABLE: "Sunucuya ulaşılamıyor.",
          KILITLI: "Kasa kilitli.",
          KURULUM_GEREKLI: "Bu cihazda kasa kurulmamış.",
          DEFTER_KAYIP: "Şifreli kasa bulunamadı.",
          BASKA_DEFTER: "Bu yedek başka bir kasaya ait.",
          UNKNOWN: "Bilinmeyen hata.",
        };
        throw rpc(options.failOpenWith, messages[options.failOpenWith], "createFakeRepository(failOpenWith)");
      }
      if (!opened) {
        opened = true;
        seedNow();
      }
      return {
        vfs: "memory (geliştirme sahtesi)",
        schemaVersion: SCHEMA_VERSION,
        persistent: false,
        estimatedQuotaBytes: null,
        deviceId,
      };
    },

    async close(): Promise<void> {
      await wait();
      opened = false;
    },

    accounts: {
      async list(includeDeleted = false, devreDisiDahil = false): Promise<readonly Account[]> {
        await wait();
        requireOpen();
        return [...accounts.values()]
          .filter((a) => includeDeleted || !a.deleted)
          // Devre dışı hesap yalnızca açıkça istenince (gerçek depo gibi; 25.10.2026).
          .filter((a) => devreDisiDahil || a.devreDisi !== true)
          .sort((a, b) => a.name.localeCompare(b.name, "tr"));
      },
      async get(id): Promise<Account | null> {
        await wait();
        requireOpen();
        return accounts.get(id) ?? null;
      },
      async create(input): Promise<Account> {
        await wait();
        requireOpen();
        if (input.name.trim() === "") throw rpc("VALIDATION", "Hesap adı boş olamaz.");
        if (accounts.has(input.id)) throw rpc("VALIDATION", "Bu kimlikte hesap var.");
        const row = stamp<Account>(input);
        accounts.set(row.id, row);
        return row;
      },
      async update(id, patch): Promise<Account> {
        await wait();
        requireOpen();
        const prev = accounts.get(id);
        if (!prev) throw rpc("NOT_FOUND", "Hesap bulunamadı.");
        // Gerçek depo gibi: `null` verilen isteğe bağlı alan SİLİNİR.
        const birlesik: Record<string, unknown> = { ...prev };
        for (const [k, v] of Object.entries(patch)) {
          if (v === null) delete birlesik[k];
          else if (v !== undefined) birlesik[k] = v;
        }
        const next = { ...birlesik, lamport: tick(), updatedAt: new Date().toISOString() } as Account;
        accounts.set(id, next);
        return next;
      },
      async remove(id, secim): Promise<void> {
        await wait();
        requireOpen();
        const prev = accounts.get(id);
        if (!prev) throw rpc("NOT_FOUND", "Hesap bulunamadı.");
        if (prev.deleted) return;
        // Gerçek depo gibi: hareketler önce silinir (varsayılan) ya da taşınır.
        hareketleriSilYaDaTasi(id, secim ?? { tur: "sil" });
        accounts.set(id, { ...prev, deleted: true, lamport: tick(), updatedAt: new Date().toISOString() });
      },
      async oksuzHareketler() {
        await wait();
        requireOpen();
        const gruplar = new Map<string, number>();
        for (const t of transactions.values()) {
          const a = accounts.get(t.accountId);
          if (t.deleted || (a !== undefined && !a.deleted)) continue;
          gruplar.set(t.accountId, (gruplar.get(t.accountId) ?? 0) + 1);
        }
        return [...gruplar].map(([accountId, hareketSayisi]) => {
          const a = accounts.get(accountId);
          return { accountId, ad: a?.name ?? accountId, ...(a?.renk === undefined ? {} : { renk: a.renk }), hareketSayisi };
        });
      },
      async oksuzHareketleriCoz(id, secim) {
        await wait();
        requireOpen();
        const a = accounts.get(id);
        if (a !== undefined && !a.deleted) throw rpc("VALIDATION", "Bu hesap silinmemiş; hareketleri öksüz değil.");
        return hareketleriSilYaDaTasi(id, secim);
      },
    },

    transactions: {
      async query(filter = {}): Promise<TransactionPage> {
        await wait();
        requireOpen();
        const devreDisi = new Set([...accounts.values()].filter((a) => a.devreDisi === true).map((a) => a.id));
        const gizle = (filter.accountIds === undefined || filter.accountIds.length === 0) && filter.devreDisiDahil !== true;
        const all = [...transactions.values()]
          .filter((t) => matches(t, filter))
          .filter((t) => !gizle || !devreDisi.has(t.accountId))
          .sort((a, b) => (a.tradeDate === b.tradeDate ? b.id.localeCompare(a.id) : b.tradeDate.localeCompare(a.tradeDate)));
        const offset = filter.offset ?? 0;
        const limit = filter.limit ?? all.length;
        return { rows: all.slice(offset, offset + limit), total: all.length };
      },
      async get(id): Promise<Transaction | null> {
        await wait();
        requireOpen();
        return transactions.get(id) ?? null;
      },
      async create(input): Promise<Transaction> {
        await wait();
        requireOpen();
        if (!accounts.has(input.accountId)) throw rpc("VALIDATION", "Hareketin hesabı yok.");
        const row = stamp<Transaction>(input);
        transactions.set(row.id, row);
        return row;
      },
      async createMany(inputs): Promise<readonly Transaction[]> {
        await wait();
        requireOpen();
        const rows = inputs.map((i) => stamp<Transaction>(i));
        for (const r of rows) transactions.set(r.id, r);
        return rows;
      },
      async update(id, patch): Promise<Transaction> {
        await wait();
        requireOpen();
        const prev = transactions.get(id);
        if (!prev) throw rpc("NOT_FOUND", "Hareket bulunamadı.");
        const next: Transaction = { ...prev, ...patch, lamport: tick(), updatedAt: new Date().toISOString() };
        transactions.set(id, next);
        return next;
      },
      async remove(id): Promise<void> {
        await wait();
        requireOpen();
        const prev = transactions.get(id);
        if (!prev) throw rpc("NOT_FOUND", "Hareket bulunamadı.");
        transactions.set(id, { ...prev, deleted: true, lamport: tick(), updatedAt: new Date().toISOString() });
      },
    },

    manualValuations: {
      async list(label): Promise<readonly ManualValuation[]> {
        await wait();
        requireOpen();
        return [...valuations.values()]
          .filter((v) => !v.deleted && (label === undefined || v.label === label))
          .sort((a, b) => b.date.localeCompare(a.date));
      },
      async upsert(input): Promise<ManualValuation> {
        await wait();
        requireOpen();
        const row = stamp<ManualValuation>(input);
        valuations.set(row.id, row);
        return row;
      },
      async remove(id): Promise<void> {
        await wait();
        requireOpen();
        const prev = valuations.get(id);
        if (!prev) throw rpc("NOT_FOUND", "Değerleme bulunamadı.");
        valuations.set(id, { ...prev, deleted: true, lamport: tick(), updatedAt: new Date().toISOString() });
      },
    },

    manualPrices: {
      async latestAsOf(instrumentRef, date): Promise<ManualPrice | null> {
        await wait();
        requireOpen();
        const candidates = [...prices.values()]
          .filter((p) => p.instrumentRef === instrumentRef && p.date <= date)
          .sort((a, b) => b.date.localeCompare(a.date));
        return candidates[0] ?? null;
      },
      async upsert(price): Promise<ManualPrice> {
        await wait();
        requireOpen();
        // Sözleşme artık yazılan kaydı döndürmemizi istiyor ve ManualPrice
        // senkron alanları taşıyor: damgalama diğer tablolarla aynı.
        const stamped = stamp<ManualPrice>(price);
        prices.set(priceKey(price.instrumentRef, price.date), stamped);
        return stamped;
      },
      async remove(instrumentRef, date): Promise<void> {
        await wait();
        requireOpen();
        const key = priceKey(instrumentRef, date);
        const prev = prices.get(key);
        if (prev === undefined) throw rpc("NOT_FOUND", "Fiyat kaydı bulunamadı.");
        // Tombstone — satır silinmiyor, işaretleniyor.
        prices.set(key, { ...prev, deleted: true, lamport: tick(), updatedAt: new Date().toISOString() });
      },
      async listFor(instrumentRef): Promise<readonly ManualPrice[]> {
        await wait();
        requireOpen();
        return [...prices.values()]
          .filter((p) => p.instrumentRef === instrumentRef)
          .sort((a, b) => b.date.localeCompare(a.date));
      },
    },

    async positions(asOf): Promise<readonly RawPosition[]> {
      await wait();
      requireOpen();
      const groups = new Map<
        string,
        { asset: AssetBinding; currency: string; amounts: string[]; first: IsoDate; last: IsoDate; count: number }
      >();

      const devreDisi = new Set([...accounts.values()].filter((a) => a.devreDisi === true).map((a) => a.id));
      for (const t of transactions.values()) {
        if (t.deleted) continue;
        if (devreDisi.has(t.accountId)) continue;
        if (asOf !== undefined && t.tradeDate > asOf) continue;

        // Miktar taşıyan türler `quantity`, nakit türleri `price` (= tutar)
        // üzerinden katkı verir. Katkısı sıfır olan türler (örn. tracked bir
        // varlığa yazılmış temettü) miktara dokunmaz.
        let contribution: string | null = null;
        if (isQuantityBearing(t.type)) {
          const sign = quantitySign(t.type);
          if (sign !== 0) contribution = sign < 0 ? decimalNegate(t.quantity) : t.quantity;
        } else {
          const sign = cashSign(t.type);
          if (sign !== 0 && t.asset.kind === "manual") {
            contribution = sign < 0 ? decimalNegate(t.price) : t.price;
          }
        }
        if (contribution === null) continue;

        const key = assetKey(t.asset);
        const existing = groups.get(key);
        if (existing) {
          existing.amounts.push(contribution);
          existing.count += 1;
          if (t.tradeDate < existing.first) existing.first = t.tradeDate;
          if (t.tradeDate > existing.last) existing.last = t.tradeDate;
        } else {
          groups.set(key, {
            asset: t.asset,
            currency: t.currency,
            amounts: [contribution],
            first: t.tradeDate,
            last: t.tradeDate,
            count: 1,
          });
        }
      }

      return [...groups.values()]
        .map((g) => ({
          asset: g.asset,
          quantity: decimalSum(g.amounts),
          currency: g.currency,
          firstTradeDate: g.first,
          lastTradeDate: g.last,
          transactionCount: g.count,
        }))
        .sort((a, b) => assetKey(a.asset).localeCompare(assetKey(b.asset), "tr"));
    },

    /**
     * DEĞERLEME SAHTEDE YOK — VE BU BİLİNÇLİ BİR EKSİKLİK.
     *
     * Buraya ikinci bir değerleme/FIFO uygulaması yazmak teknik olarak
     * mümkündü ve TAM OLARAK bu proje bir kez o bedeli ödedi: sahte deponun
     * kendi yedek biçimi gerçek çekirdekten ayrıştı, `formatVersion` alanı
     * eksik kaldı ve sahte modda alınan bir yedek gerçek çekirdekle geri
     * yüklenemedi. Kusur aylarca görünmedi çünkü iki uygulama da KENDİ
     * içinde tutarlıydı.
     *
     * Vergi hesabında bu bedel kıyaslanamaz ölçüde yüksek: sahtenin FIFO'su
     * gerçeğinden bir kuruş sapsa, arayüz testleri geçerken kullanıcının
     * gördüğü sayı yanlış olurdu.
     *
     * İKİNCİ SEBEP: sahte deponun fiyat önbelleğine erişimi YOK
     * (`createFakePriceCache` ayrı bir nesne ve `PriceCachePort` tek gün
     * sorgusu — `closeAsOf`/`fxAsOf` — sunmuyor). Yani buradaki bir değerleme
     * fiyatları da UYDURMAK zorunda kalırdı.
     *
     * J ŞERİDİNİN İLK İŞİ: gerçek motoru (`/parafon-db/valuation.js`)
     * `lib/pfai.ts`teki desenle çalışma zamanında yükleyip sahte ölçümlerle
     * beslemek, ya da `PriceCachePort`u tek gün sorgularıyla genişletmek.
     * İkisi de bu dosyaya kod yazmaktan iyi.
     */
    async valuation(): Promise<never> {
      await wait();
      requireOpen();
      throw rpc(
        "UNKNOWN",
        "Değerleme sahte depoda yok: motor gerçek çekirdekte yaşıyor ve buraya " +
          "ikinci bir kopyası YAZILMADI (sapma riski).",
        "fakeRepository.valuation — gerçek çekirdekle çalıştırın (npm -w @parafon/web run sync:db)",
      );
    },

    // Seri de değerlemedir: aynı gerekçeyle sahtede YOK (testler gerçek motoru
    // `test/reportEngine.ts` → `buildSeries` ile koşturuyor).
    async valuationSeries(): Promise<never> {
      await wait();
      requireOpen();
      throw rpc(
        "UNKNOWN",
        "Günlük seri sahte depoda yok: motor gerçek çekirdekte yaşıyor.",
        "fakeRepository.valuationSeries — gerçek çekirdekle çalıştırın",
      );
    },

    async fifo(): Promise<never> {
      await wait();
      requireOpen();
      throw rpc(
        "UNKNOWN",
        "FIFO parti dökümü sahte depoda yok: motor gerçek çekirdekte yaşıyor.",
        "fakeRepository.fifo — gerçek çekirdekle çalıştırın (npm -w @parafon/web run sync:db)",
      );
    },

    settings: {
      async get(key): Promise<string | null> {
        await wait();
        return settings.get(key) ?? null;
      },
      async set(key, value): Promise<void> {
        await wait();
        settings.set(key, value);
      },
      async all(): Promise<Readonly<Record<string, string>>> {
        await wait();
        return Object.fromEntries(settings);
      },
    },

    async exportPlainJson(): Promise<string> {
      await wait();
      requireOpen();
      // ZARF GERÇEK ÇEKİRDEKLE BİREBİR: gerçek `importPlain` `format` VE
      // `formatVersion` istiyor, `settings`i de DİZİ olarak okuyor. Bu sahte
      // eskiden `formatVersion` yazmıyor ve ayarları nesne olarak veriyordu —
      // yani sahte modda alınan bir yedek (ya da ondan üretilen bir `.pfai`)
      // gerçek çekirdeğe geri yüklenemezdi. Sahtenin ürettiği dosya gerçek
      // uygulamada açılamıyorsa "geliştirme kolaylığı" değil tuzaktır.
      const now = new Date().toISOString();
      return JSON.stringify(
        {
          format: "parafon-plain-json",
          formatVersion: 1,
          schemaVersion: SCHEMA_VERSION,
          exportedAt: now,
          exportedByDeviceId: deviceId,
          accounts: [...accounts.values()],
          transactions: [...transactions.values()],
          manualValuations: [...valuations.values()],
          manualPrices: [...prices.values()],
          // Sahtede ayarların senkron damgası YOK (Map<string,string>).
          // Damgalar burada üretiliyor; gerçek çekirdekte satırın kendi
          // damgası korunur. Bilinçli ve sahteye özgü bir gevşeklik.
          settings: [...settings.entries()].map(([key, value]) => ({
            key,
            value,
            lamport: tick(),
            deviceId,
            deleted: false,
            updatedAt: now,
          })),
        },
        null,
        2,
      );
    },

    /**
     * SAHTE BİLE OLSA LWW UYGULAR — ve bunun bir sebebi var.
     *
     * Eskiden bu sahte gelen her kaydı koşulsuz yazıyordu. Sonuç: aynı
     * cihazdan alınmış bir yedeği geri yüklemek sahtede "1 hesap, 1 hareket"
     * derken gerçek çekirdekte "0, 0" diyordu (kayıtlar zaten aynıydı) ve
     * arayüz testleri bu farkı GÖREMEDİĞİ için kusur canlıda bulundu.
     * Sahtenin işi arayüzü beslemek; ama sözleşmenin GÖZLENEBİLİR davranışını
     * yanlış taklit ederse test değeri negatife düşer.
     *
     * Kalan bilinçli gevşeklik: damgası olmayan (lamport/deviceId taşımayan)
     * kayıtlar burada yazılır, gerçek çekirdekte reddedilir — sahte elle
     * yazılmış test yüklerini kabul edebilsin diye.
     */
    /**
     * Gerçek deponun (`Store.iceAktar`) davranışının sahtesi: kimliğe göre
     * yeni / aynı / güncellenecek / silinmiş; silinmiş diriltilmez.
     */
    async iceAktar(paket, secenek = {}): Promise<IceAktarmaSonucu> {
      await wait();
      requireOpen();
      const sade = (o: object): string =>
        JSON.stringify(o, (k, v: unknown) =>
          ["lamport", "deviceId", "deleted", "updatedAt"].includes(k)
            ? undefined
            : typeof v === "string" && /^-?\d+\.\d+$/.test(v)
              ? v.replace(/0+$/, "").replace(/\.$/, "")
              : v,
        );
      const sinif = <T extends { id: string; deleted: boolean }>(m: Map<string, T>, k: { id: string }): IceAktarmaDurumu => {
        const v = m.get(k.id);
        if (v === undefined) return "yeni";
        if (v.deleted) return "silinmis";
        const anahtarlar = Object.keys(k).filter((x) => x !== "id");
        const mevcut = Object.fromEntries(anahtarlar.map((x) => [x, (v as Record<string, unknown>)[x]]));
        const gelen = Object.fromEntries(anahtarlar.map((x) => [x, (k as Record<string, unknown>)[x]]));
        return sade(mevcut) === sade(gelen) ? "ayni" : "guncellenecek";
      };
      const h: Record<string, IceAktarmaDurumu> = {};
      for (const a of paket.accounts) h[a.id] = sinif(accounts, a);
      const t: Record<string, IceAktarmaDurumu> = {};
      for (const x of paket.transactions) {
        const hesapVar = (h[x.accountId] !== undefined && h[x.accountId] !== "silinmis") || accounts.get(x.accountId)?.deleted === false;
        if (!hesapVar) throw rpc("VALIDATION", `${x.id} hareketinin hesabı yok ya da silinmiş: ${x.accountId}`);
        t[x.id] = sinif(transactions, x);
      }
      if (secenek.deneme !== true) {
        for (const a of paket.accounts) {
          if (h[a.id] === "yeni" || h[a.id] === "guncellenecek") accounts.set(a.id, stamp<Account>(a));
        }
        for (const x of paket.transactions) {
          if (t[x.id] === "yeni" || t[x.id] === "guncellenecek") transactions.set(x.id, stamp<Transaction>(x));
        }
      }
      return { accounts: h, transactions: t, yazildi: secenek.deneme !== true };
    },

    async importPlainJson(json): Promise<ImportResult> {
      await wait();
      requireOpen();
      const skipped: { kind: string; id: string; reason: string }[] = [];
      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch {
        throw rpc("VALIDATION", "Dosya geçerli JSON değil.");
      }
      if (typeof parsed !== "object" || parsed === null) throw rpc("VALIDATION", "Beklenen biçim bir nesne.");
      const doc = parsed as Record<string, unknown>;

      const bos = (): { found: number; written: number; unchanged: number; skipped: number } => ({
        found: 0,
        written: 0,
        unchanged: 0,
        skipped: 0,
      });
      const rapor = {
        accounts: bos(),
        transactions: bos(),
        manualValuations: bos(),
        manualPrices: bos(),
        settings: bos(),
      };
      type Tur = keyof typeof rapor;

      const oku = (tur: Tur, key: string): readonly unknown[] => {
        const rows = asArray(doc[key]);
        rapor[tur].found += rows.length;
        return rows;
      };
      const atla = (tur: Tur, kind: string, id: string, reason: string): void => {
        rapor[tur].skipped += 1;
        skipped.push({ kind, id, reason });
      };
      /** Gerçek çekirdekteki üç durumlu kararın aynısı. */
      const karar = (
        yerel: { lamport?: number; deviceId?: string } | undefined,
        gelen: { lamport?: number; deviceId?: string },
      ): "yaz" | "ayni" | "yerel-yeni" => {
        if (yerel === undefined) return "yaz";
        if (typeof yerel.lamport !== "number" || typeof gelen.lamport !== "number") return "yaz";
        if (yerel.lamport === gelen.lamport && yerel.deviceId === gelen.deviceId) return "ayni";
        if (yerel.lamport !== gelen.lamport) return gelen.lamport > yerel.lamport ? "yaz" : "yerel-yeni";
        return (gelen.deviceId ?? "") > (yerel.deviceId ?? "") ? "yaz" : "yerel-yeni";
      };
      const uygula = <T extends { lamport?: number; deviceId?: string }>(
        tur: Tur,
        kind: string,
        id: string,
        yerel: T | undefined,
        gelen: T,
        yaz: () => void,
      ): void => {
        const k = karar(yerel, gelen);
        if (k === "ayni") {
          rapor[tur].unchanged += 1;
          return;
        }
        if (k === "yerel-yeni") {
          atla(tur, kind, id, "yerel kayıt daha yeni (lamport/deviceId)");
          return;
        }
        yaz();
        rapor[tur].written += 1;
      };

      for (const raw of oku("accounts", "accounts")) {
        const row = raw as Account;
        if (typeof row?.id !== "string" || typeof row?.name !== "string") {
          atla("accounts", "account", String((raw as { id?: unknown })?.id ?? "?"), "eksik alan");
          continue;
        }
        uygula("accounts", "account", row.id, accounts.get(row.id), row, () => {
          accounts.set(row.id, row);
        });
      }
      for (const raw of oku("transactions", "transactions")) {
        const row = raw as Transaction;
        if (typeof row?.id !== "string" || typeof row?.accountId !== "string") {
          atla("transactions", "transaction", String((raw as { id?: unknown })?.id ?? "?"), "eksik alan");
          continue;
        }
        uygula("transactions", "transaction", row.id, transactions.get(row.id), row, () => {
          transactions.set(row.id, row);
        });
      }
      for (const raw of oku("manualValuations", "manualValuations")) {
        const row = raw as ManualValuation;
        if (typeof row?.id !== "string") {
          atla("manualValuations", "manualValuation", "?", "eksik alan");
          continue;
        }
        uygula("manualValuations", "manualValuation", row.id, valuations.get(row.id), row, () => {
          valuations.set(row.id, row);
        });
      }
      for (const raw of oku("manualPrices", "manualPrices")) {
        const row = raw as ManualPrice;
        if (typeof row?.instrumentRef !== "string" || typeof row?.date !== "string") {
          atla("manualPrices", "manualPrice", "?", "eksik alan");
          continue;
        }
        const key = priceKey(row.instrumentRef, row.date);
        uygula("manualPrices", "manualPrice", key, prices.get(key), row, () => {
          prices.set(key, row);
        });
      }
      // Ayarlar: sahtede damga tutulmadığı için LWW uygulanamıyor, gelen yazar.
      for (const raw of oku("settings", "settings")) {
        const row = raw as { key?: unknown; value?: unknown };
        if (typeof row?.key !== "string" || typeof row?.value !== "string") {
          atla("settings", "setting", String(row?.key ?? "?"), "eksik alan");
          continue;
        }
        if (row.key.startsWith("__parafon.")) {
          atla("settings", "setting", row.key, "çekirdeğe ait ayar — ezilmez");
          continue;
        }
        settings.set(row.key, row.value);
        rapor.settings.written += 1;
      }

      return { ...rapor, skipped };
    },
  };

  return repo;
}

function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}
